import { useState } from 'react';
import { useTransactions, useAddTransaction, useDeleteTransaction, useYearlyTransactions, useTransactionYears } from '../hooks/useTransactions';
import { useAuthStore } from '../store/authStore';
import { useSettingsStore } from '../store/settingsStore';
import { useT } from '../hooks/useT';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { exportTransactionsMonthPdf, exportTransactionsYearPdf } from '../utils/exportPdf';

const CATEGORIES = [
  { slug: 'food',          icon: '🍽️' },
  { slug: 'transport',     icon: '🚗' },
  { slug: 'housing',       icon: '🏠' },
  { slug: 'utilities',     icon: '💡' },
  { slug: 'health',        icon: '🏥' },
  { slug: 'entertainment', icon: '🎬' },
  { slug: 'education',     icon: '📚' },
  { slug: 'clothing',      icon: '👗' },
  { slug: 'personal_care', icon: '💆' },
  { slug: 'subscriptions', icon: '📱' },
  { slug: 'investments',   icon: '📈' },
  { slug: 'other',         icon: '📦' },
];

const CATEGORY_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.slug, c]));

function fmt(value, currency = 'USD') {
  const locale = currency === 'BRL' ? 'pt-BR' : currency === 'EUR' ? 'de-DE' : 'en-US';
  return parseFloat(value || 0).toLocaleString(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  });
}

function formatDay(dateStr, locale) {
  // dateStr from DB is YYYY-MM-DD; append noon to avoid timezone shift
  return new Date(dateStr.slice(0, 10) + 'T12:00:00').toLocaleDateString(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

const today = new Date();

export default function TransactionsPage() {
  const { user, isPaid } = useAuthStore();
  const language = useSettingsStore((s) => s.language);
  const t = useT();
  const currency = user?.currency || 'USD';
  const locale = language === 'pt-BR' ? 'pt-BR' : 'en-US';
  const paid = isPaid();

  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [showForm, setShowForm] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportYear, setExportYear] = useState(today.getFullYear());
  const [isExporting, setIsExporting] = useState(false);
  const [form, setForm] = useState({
    amount: '',
    category: 'food',
    description: '',
    tx_date: today.toISOString().slice(0, 10),
    type: 'expense',
  });
  const [formError, setFormError] = useState(null);

  const { data, isLoading } = useTransactions(year, month);
  const { mutateAsync: addTransaction, isPending: isAdding } = useAddTransaction();
  const { mutateAsync: deleteTransaction } = useDeleteTransaction();
  const { data: availableYears } = useTransactionYears();
  const { data: yearlyData } = useYearlyTransactions(exportYear, showExportMenu && paid);

  const transactions = data?.transactions || [];
  const summary = data?.summary || { total_expenses: 0, total_income: 0, total_count: 0 };

  // Group by date (most recent first)
  const grouped = transactions.reduce((acc, tx) => {
    const key = tx.tx_date.slice(0, 10);
    if (!acc[key]) acc[key] = [];
    acc[key].push(tx);
    return acc;
  }, {});
  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  const monthLabel = (() => {
    const s = new Date(year, month - 1, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    return s.charAt(0).toUpperCase() + s.slice(1);
  })();
  const net = parseFloat(summary.total_income) - parseFloat(summary.total_expenses);

  function prevMonth() {
    if (month === 1) { setYear((y) => y - 1); setMonth(12); }
    else { setMonth((m) => m - 1); }
  }

  function nextMonth() {
    if (month === 12) { setYear((y) => y + 1); setMonth(1); }
    else { setMonth((m) => m + 1); }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError(null);
    const parsed = parseFloat(form.amount.replace(',', '.'));
    if (isNaN(parsed) || parsed <= 0) {
      setFormError(t('tx.form.error.amount'));
      return;
    }
    try {
      await addTransaction({ ...form, amount: parsed });
      setForm((f) => ({ ...f, amount: '', description: '' }));
      setShowForm(false);
    } catch (err) {
      setFormError(err.response?.data?.error?.message || t('tx.form.error.add'));
    }
  }

  async function handleDelete(id) {
    if (!window.confirm(t('tx.delete.confirm'))) return;
    await deleteTransaction(id);
  }

  async function handleExportMonth() {
    setIsExporting(true);
    try {
      exportTransactionsMonthPdf({ transactions, summary }, year, month, currency, locale);
    } finally {
      setIsExporting(false);
      setShowExportMenu(false);
    }
  }

  async function handleExportYear() {
    setIsExporting(true);
    try {
      exportTransactionsYearPdf(yearlyData, exportYear, currency, locale);
    } finally {
      setIsExporting(false);
      setShowExportMenu(false);
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">{t('tx.title')}</h1>
          <p className="text-slate-400 mt-1">{t('tx.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Export PDF dropdown */}
          <div className="relative">
            <Button
              variant="secondary"
              size="sm"
              disabled={!paid}
              title={!paid ? t('tx.export_pdf.pro_only') : undefined}
              onClick={() => paid && setShowExportMenu((v) => !v)}
              isLoading={isExporting}
            >
              ⬇ {t('tx.export_pdf')}
            </Button>

            {showExportMenu && (
              <div className="absolute right-0 top-full mt-1 z-20 w-64 bg-surface-card border border-surface-border rounded-xl shadow-xl p-3 space-y-3">
                {/* Month export */}
                <button
                  onClick={handleExportMonth}
                  className="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-surface-hover transition-colors"
                >
                  📄 {t('tx.export_pdf.month')} ({monthLabel})
                </button>

                <div className="border-t border-surface-border pt-3 space-y-2">
                  <p className="text-xs text-slate-500">{t('tx.export_pdf.year')}:</p>
                  {availableYears?.length ? (
                    <>
                      <select
                        value={exportYear}
                        onChange={(e) => setExportYear(Number(e.target.value))}
                        className="w-full bg-surface border border-surface-border rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        {availableYears.map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                      <button
                        onClick={handleExportYear}
                        disabled={!yearlyData}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-surface-hover transition-colors disabled:opacity-40"
                      >
                        📊 {t('tx.export_pdf.select_year')} {exportYear}
                      </button>
                    </>
                  ) : (
                    <p className="text-xs text-slate-500 italic">{t('tx.export_pdf.no_years')}</p>
                  )}
                </div>

                <button
                  onClick={() => setShowExportMenu(false)}
                  className="absolute top-2 right-2 text-slate-500 hover:text-slate-300 text-lg leading-none"
                >×</button>
              </div>
            )}
          </div>

          <Button onClick={() => setShowForm((v) => !v)}>
            {showForm ? t('tx.cancel') : t('tx.add')}
          </Button>
        </div>
      </div>

      {/* Add transaction form */}
      {showForm && (
        <div className="card space-y-4">
          <h3 className="font-semibold text-slate-200">{t('tx.form.title')}</h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Type toggle */}
            <div className="sm:col-span-2 flex gap-2">
              {['expense', 'income'].map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, type }))}
                  className={`px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${
                    form.type === type
                      ? type === 'expense'
                        ? 'bg-red-600/20 text-red-400 border-red-700'
                        : 'bg-green-600/20 text-green-400 border-green-700'
                      : 'text-slate-400 border-surface-border hover:text-slate-200'
                  }`}
                >
                  {type === 'expense' ? t('tx.form.expense') : t('tx.form.income')}
                </button>
              ))}
            </div>

            <Input
              label={t('tx.form.amount')}
              type="number"
              min="0.01"
              step="0.01"
              placeholder="0.00"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              required
            />

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">{t('tx.form.category')}</label>
              <select
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                className="w-full bg-surface border border-surface-border rounded-xl px-3 py-2.5 text-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.icon} {t(`expense.cat.${c.slug}`)}</option>
                ))}
              </select>
            </div>

            <Input
              label={t('tx.form.description')}
              placeholder={t('tx.form.description_placeholder')}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />

            <Input
              label={t('tx.form.date')}
              type="date"
              value={form.tx_date}
              onChange={(e) => setForm((f) => ({ ...f, tx_date: e.target.value }))}
              required
            />

            {formError && (
              <p className="sm:col-span-2 text-red-400 text-sm">{formError}</p>
            )}

            <div className="sm:col-span-2">
              <Button type="submit" isLoading={isAdding} className="w-full">
                {form.type === 'expense' ? t('tx.form.submit.expense') : t('tx.form.submit.income')}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Month navigator */}
      <div className="flex items-center justify-between card py-3 px-5">
        <button
          onClick={prevMonth}
          className="text-slate-400 hover:text-slate-200 text-xl px-2 transition-colors"
          aria-label="Previous month"
        >
          ‹
        </button>
        <h2 className="font-semibold text-slate-200">{monthLabel}</h2>
        <button
          onClick={nextMonth}
          className="text-slate-400 hover:text-slate-200 text-xl px-2 transition-colors"
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card">
          <p className="text-xs text-slate-500 mb-1">{t('tx.summary.spent')}</p>
          <p className="text-xl font-bold text-red-400">{fmt(summary.total_expenses, currency)}</p>
        </div>
        <div className="card">
          <p className="text-xs text-slate-500 mb-1">{t('tx.summary.income')}</p>
          <p className="text-xl font-bold text-green-400">{fmt(summary.total_income, currency)}</p>
        </div>
        <div className="card">
          <p className="text-xs text-slate-500 mb-1">{t('tx.summary.net')}</p>
          <p className={`text-xl font-bold ${net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {fmt(net, currency)}
          </p>
        </div>
      </div>

      {/* Transactions list */}
      {isLoading ? (
        <div className="space-y-3 animate-pulse">
          {[1, 2, 3].map((i) => <div key={i} className="h-16 bg-surface-card rounded-xl" />)}
        </div>
      ) : transactions.length === 0 ? (
        <div className="card text-center py-14">
          <p className="text-4xl mb-3">💳</p>
          <p className="text-slate-300 font-semibold">{t('tx.empty.title').replace('{month}', monthLabel)}</p>
          <p className="text-slate-500 text-sm mt-2">
            {t('tx.empty.sub')}
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="mt-6 btn-primary text-sm"
          >
            {t('tx.empty.cta')}
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          {sortedDates.map((date) => (
            <div key={date}>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-1">
                {formatDay(date, locale)}
              </p>
              <div className="card p-0 overflow-hidden divide-y divide-surface-border">
                {grouped[date].map((tx) => {
                  const cat = CATEGORY_MAP[tx.category] || CATEGORY_MAP.other;
                  return (
                    <div
                      key={tx.id}
                      className="flex items-center gap-4 px-4 py-3 hover:bg-surface-hover transition-colors"
                    >
                      <span className="text-xl w-8 text-center flex-shrink-0">{cat.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-200 truncate">
                          {tx.description || t(`expense.cat.${cat.slug}`)}
                        </p>
                        <p className="text-xs text-slate-500">{t(`expense.cat.${cat.slug}`)}</p>
                      </div>
                      <p className={`font-semibold text-sm flex-shrink-0 ${
                        tx.type === 'income' ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {tx.type === 'income' ? '+' : '-'}{fmt(tx.amount, currency)}
                      </p>
                      <button
                        onClick={() => handleDelete(tx.id)}
                        className="text-slate-600 hover:text-red-400 transition-colors text-lg leading-none flex-shrink-0"
                        title={t('tx.delete.tooltip')}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
